/**
 * Sign-in. The console reads the tenant's discovery document from the API,
 * builds an MSAL configuration from it (Entra issuer -> authority, public
 * client id, API scope) and signs the person in with a redirect. Any OIDC
 * issuer works the same way as long as it supports the authorisation-code
 * flow with PKCE. With VITE_DEV_TOKEN set, sign-in is skipped and that
 * bearer is sent instead (the API must run with AIOE_DEV_AUTH=1).
 *
 * Mojo Up AI Cloud (`edition: "cloud"` in the discovery document) signs in with
 * its own tokens instead (CloudAuthProvider.tsx); MSAL is then never loaded.
 *
 * Signing out never signs the person straight back in. Microsoft returns them to `/` (the one
 * address the app registration lists), so a flag in sessionStorage says this tab has just signed
 * out, and the console shows the signed-out page at `/signed-out` instead of starting a new
 * sign-in. That address never signs in by itself. A first visit in a browser shows a sign-in page
 * with one button; once someone has signed in here before, the console goes straight on.
 */

import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import type { DiscoveryDocument } from '@aioe/shared';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { routes, setTokenProvider } from '../api/client.ts';
import { GRAPH_SCOPES, photoOf, setGraphTokenProvider } from '../directory.ts';
import { setEdition } from '../edition.ts';
import { cloudSession } from './cloudSession.ts';
import { CloudAuthProvider } from './CloudAuthProvider.tsx';
import { AuthContext, type AuthState, RENEWAL_MESSAGE, RENEWED_EVENT, SIGNED_OUT_PATH } from './context.ts';

export { RENEWED_EVENT, SIGNED_OUT_PATH, useAuth, useCloud } from './context.ts';
export type { AuthState, CloudState } from './context.ts';

// Only a dev server honours VITE_DEV_TOKEN. A production bundle ignores it, so a token left in a
// .env file can never be baked into a deployed console.
const DEV_TOKEN = import.meta.env.DEV ? (import.meta.env.VITE_DEV_TOKEN as string | undefined) : undefined;

/** Set just before Microsoft's sign-out, read once on the way back. */
const ENDED_KEY = 'aioe.signedOut';
/** Someone has signed in on this browser before, so a visit continues to Microsoft without asking. */
const SIGNED_IN_BEFORE_KEY = 'aioe.signedInBefore';

function storage(kind: 'local' | 'session'): Storage | undefined {
  try {
    return kind === 'local' ? localStorage : sessionStorage;
  } catch {
    return undefined;
  }
}

function authorityFor(issuer: string): string {
  // Entra's v2.0 issuer is the authority without the version suffix.
  return issuer.replace(/\/v2\.0\/?$/, '');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, 'signOut'>>({ ready: false });
  const [cloud, setCloud] = useState<DiscoveryDocument>();
  const [client, setClient] = useState<PublicClientApplication>();
  const [renewal, setRenewal] = useState<{ renew(): Promise<void> }>();
  const [stepUp, setStepUp] = useState<{ message: string; confirm(): Promise<void>; cancel(): void }>();
  /** A fresh interactive sign-in (Microsoft asks for the password or passkey again), once MSAL is ready. */
  const interactive = useRef<() => Promise<void>>(undefined);
  const [canStepUp, setCanStepUp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let discovery: DiscoveryDocument | undefined;
      try {
        discovery = await routes.discovery();
      } catch (e) {
        if (!cancelled) setState({ ready: false, failure: 'discovery', error: `The API at ${location.origin} has no tenant for this host (${(e as Error).message}).` });
        return;
      }
      setEdition(discovery.edition);
      if (discovery.edition === 'cloud' && !DEV_TOKEN) {
        setTokenProvider((opts) => cloudSession.token(opts));
        if (!cancelled) setCloud(discovery);
        return;
      }
      if (DEV_TOKEN) {
        setTokenProvider(async () => DEV_TOKEN);
        const [, , id, email] = DEV_TOKEN.split(':');
        if (!cancelled) setState({ ready: true, discovery, account: { name: 'Development user', email, id } });
        return;
      }
      // Loaded only here, so Mojo Up AI Cloud never downloads or starts MSAL.
      const { BrowserAuthError, InteractionRequiredAuthError, PublicClientApplication } = await import('@azure/msal-browser');
      const pca = new PublicClientApplication({
        auth: { clientId: discovery.auth.clientId, authority: authorityFor(discovery.auth.issuer), redirectUri: `${location.origin}/`, postLogoutRedirectUri: `${location.origin}/` },
        cache: { cacheLocation: 'sessionStorage' },
      });
      await pca.initialize();
      const result = await pca.handleRedirectPromise();
      const account: AccountInfo | null = result?.account ?? pca.getAllAccounts()[0] ?? null;
      if (!account) {
        // Back from Microsoft's sign-out: show that, at an address a reload will not sign in from.
        let ended: 'manual' | 'idle' | undefined;
        try {
          const flag = storage('session')?.getItem(ENDED_KEY);
          storage('session')?.removeItem(ENDED_KEY);
          ended = flag === 'idle' ? 'idle' : flag ? 'manual' : undefined;
        } catch {
          /* storage unavailable */
        }
        if (ended) history.replaceState(null, '', `${SIGNED_OUT_PATH}${ended === 'idle' ? '?idle=1' : ''}`);
        let before = false;
        try {
          before = storage('local')?.getItem(SIGNED_IN_BEFORE_KEY) === '1';
        } catch {
          /* storage unavailable */
        }
        if (!ended && before && location.pathname !== SIGNED_OUT_PATH) {
          await pca.loginRedirect({ scopes: [discovery.auth.apiScope] });
          return;
        }
        if (!cancelled) {
          setClient(pca);
          setState({ ready: true, discovery, signedOut: true, ...(ended ? { ended } : {}) });
        }
        return;
      }
      try {
        storage('local')?.setItem(SIGNED_IN_BEFORE_KEY, '1');
      } catch {
        /* storage unavailable: the next visit shows the sign-in page again */
      }
      pca.setActiveAccount(account);
      const scopes = [discovery.auth.apiScope];
      interactive.current = async () => {
        try {
          await pca.acquireTokenPopup({ scopes, account: account!, prompt: 'login' });
        } catch (e) {
          // A blocked popup: go to Microsoft and back; the page comes back where it was.
          if (e instanceof BrowserAuthError && e.errorCode === 'popup_window_error') await pca.acquireTokenRedirect({ scopes, account: account!, prompt: 'login', redirectStartPage: location.href });
          throw e;
        }
      };
      setCanStepUp(true);
      // A request never goes out without a token once signed in: a silent renewal that fails is
      // retried against the identity provider, then the person is asked, once however many requests
      // are waiting, to renew their sign-in (RenewalDialog). Nothing redirects by itself, so nothing
      // typed is lost: Continue renews in a popup, and only if the browser blocks it does the page go
      // to Microsoft and back, with drafts kept in sessionStorage (useDraft). Waiting requests fail
      // with a plain sentence rather than reaching the API bare and coming back 401.
      let asking = false;
      setTokenProvider(async (opts) => {
        try {
          return (await pca.acquireTokenSilent({ scopes, account: account!, forceRefresh: Boolean(opts?.forceRefresh) })).accessToken;
        } catch (first) {
          let e: unknown = first;
          if (!opts?.forceRefresh) {
            try {
              return (await pca.acquireTokenSilent({ scopes, account: account!, forceRefresh: true })).accessToken;
            } catch (second) {
              e = second;
            }
          }
          if (!asking && (e instanceof InteractionRequiredAuthError || e instanceof BrowserAuthError) && !cancelled) {
            asking = true;
            setRenewal({
              async renew() {
                try {
                  await pca.acquireTokenPopup({ scopes, account: account! });
                  asking = false;
                  setRenewal(undefined);
                  window.dispatchEvent(new Event(RENEWED_EVENT));
                } catch {
                  // A blocked or closed popup: go to Microsoft and back. Drafts are in sessionStorage.
                  await pca.acquireTokenRedirect({ scopes, account: account!, redirectStartPage: location.href }).catch(() => {
                    asking = false;
                  });
                }
              },
            });
          }
          throw new Error(RENEWAL_MESSAGE, { cause: first });
        }
      });
      // The directory (people and group pickers) uses the person's own Graph token. Consent is
      // granted for the tenant; if it is not, one popup asks, and the pickers fall back to ids.
      setGraphTokenProvider(async () => {
        try {
          return (await pca.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: account! })).accessToken;
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            try {
              return (await pca.acquireTokenPopup({ scopes: GRAPH_SCOPES, account: account! })).accessToken;
            } catch {
              return undefined;
            }
          }
          return undefined;
        }
      });
      if (!cancelled) {
        setClient(pca);
        setState({ ready: true, discovery, account: { name: account.name, email: account.username, id: account.localAccountId } });
        // The profile picture, with the directory's Graph token. Silent only: a picture is never worth a popup.
        void pca
          .acquireTokenSilent({ scopes: GRAPH_SCOPES, account })
          .then((t) => photoOf(t.accessToken))
          .catch(() => undefined)
          .then((picture) => {
            if (picture && !cancelled) setState((s) => (s.account ? { ...s, account: { ...s.account, picture } } : s));
          });
      }
    })().catch((e: Error) => {
      if (!cancelled) setState({ ready: false, error: e.message });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      ...state,
      ...(renewal ? { renewal } : {}),
      ...(stepUp ? { stepUp } : {}),
      ...(canStepUp
        ? {
            signInAgain: (message: string) =>
              new Promise<void>((resolve, reject) => {
                setStepUp({
                  message,
                  async confirm() {
                    await interactive.current!();
                    setStepUp(undefined);
                    resolve();
                  },
                  cancel() {
                    setStepUp(undefined);
                    reject(new Error(message));
                  },
                });
              }),
          }
        : {}),
      signIn() {
        if (!client || !state.discovery) return;
        // From the signed-out page, come back to the console's start rather than to that page.
        const start = location.pathname === SIGNED_OUT_PATH ? `${location.origin}/` : location.href;
        void client.loginRedirect({ scopes: [state.discovery.auth.apiScope], redirectStartPage: start });
      },
      signOut(opts) {
        if (!client) {
          location.reload();
          return;
        }
        try {
          storage('session')?.setItem(ENDED_KEY, opts?.idle ? 'idle' : 'manual');
        } catch {
          /* storage unavailable: Microsoft's own page then says the person signed out */
        }
        void client.logoutRedirect();
      },
    }),
    [state, client, renewal, stepUp, canStepUp],
  );
  if (cloud) return <CloudAuthProvider discovery={cloud}>{children}</CloudAuthProvider>;
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
